import { Card, Dialog, makeStyles, Typography } from "@material-ui/core";
import cx from "clsx";
import DOMPurify from "isomorphic-dompurify";
import { FunctionComponent, useState } from "react";
import { useTrackEngagement } from "react-context/legacy/TrackContentModuleEngagementContextProvider";
import useAnalytics from "react-hooks/useAnalytics";
import { ResourceCustomTheme } from "styles/config";

import styles from "./InfoCard.module.scss";

const useStyles = makeStyles((theme: ResourceCustomTheme) => ({
  root: {},
  cardDescription: {
    "& a": {
      color: theme.colors.RoyalPurple(),
      textDecoration: "underline",
    },
  },
}));

export type InfoCardProps = {
  icon?: string;
  title?: string;
  description?: string;
  header?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  emoji?: string;
};

const InfoCard: FunctionComponent<InfoCardProps> = ({
  icon,
  title,
  description,
  header,
}) => {
  const classes = useStyles();
  const { trackEngagement } = useTrackEngagement();
  const [open, setOpen] = useState(false);
  const analytics = useAnalytics();
  const sanitizedDescription = DOMPurify.sanitize(description || "", {
    ALLOWED_TAGS: ["br"],
  });

  const sanitizedDescriptionWithLinks = DOMPurify.sanitize(description || "", {
    ALLOWED_TAGS: ["a", "div", "br"],
    ALLOWED_ATTR: ["target", "href"],
  });

  return (
    <>
      <Card
        className={cx(
          styles.card,
          !sanitizedDescription ? styles.cardWithoutDescription : "",
          !icon ? styles.cardWithoutIcon : ""
        )}
        key={title}
        onClick={(): void => {
          setOpen(true);
          analytics.track("Card Clicked", {
            sectionHeader: header,
            cardTitle: title,
          });
          trackEngagement?.({ event: "Content Card Clicked" });
        }}
      >
        {icon && (
          <Typography className={styles.cardIcon} variant="h3">
            {icon}
          </Typography>
        )}
        {title && (
          <Typography className={styles.cardTitle} variant="h5">
            {title}
          </Typography>
        )}
        {sanitizedDescription && (
          <Typography className={styles.cardDescription} variant="subtitle1">
            <span dangerouslySetInnerHTML={{ __html: sanitizedDescription }} />
          </Typography>
        )}
      </Card>
      <Dialog
        className={styles.cardDialogBackground}
        onClose={(): void => setOpen(false)}
        open={open}
        aria-labelledby="simple-dialog-title"
        fullWidth
      >
        <div className={styles.cardDialog}>
          {icon && (
            <Typography className={styles.cardIcon} variant="h2">
              {icon}
            </Typography>
          )}
          {title && (
            <Typography className={styles.cardTitle} variant="h4">
              {title}
            </Typography>
          )}
          {sanitizedDescriptionWithLinks && (
            <Typography
              className={cx(
                styles.cardDescription,
                classes.cardDescription,
                "max-w-full overflow-hidden whitespace-pre-wrap"
              )}
              variant="body1"
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizedDescriptionWithLinks,
                }}
              />
            </Typography>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default InfoCard;
